import React, { useCallback, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useI18n } from '../../context/I18nContext';
import { useResetOnBfcacheRestore } from '../../hooks/useResetOnBfcacheRestore';
import { authColors, shadows } from '../../styles/colors';
import LoadingWithHelp from '../ui/LoadingWithHelp';
import OtpVerification from './OtpVerification';

interface LoginFormProps {
  onSwitchToSignUp?: () => void;
  onSwitchToForgotPassword?: () => void;
  showSignUpButton?: boolean;
}

const LoginForm: React.FC<LoginFormProps> = ({ onSwitchToSignUp, onSwitchToForgotPassword, showSignUpButton = true }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState('');
  const [showOtp, setShowOtp] = useState(false);
  const { signIn, signInWithGoogle, sendOtp } = useAuth();
  const { t } = useI18n();

  useResetOnBfcacheRestore(useCallback(() => setGoogleLoading(false), []));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const { error: signInError } = await signIn(email, password);
    
    if (signInError) {
      const errorMessage = signInError.message || '';
      // Check if email is not confirmed - user created account but didn't complete OTP verification
      const isEmailNotConfirmed = 
        errorMessage.toLowerCase().includes('email not confirmed') ||
        errorMessage.toLowerCase().includes('email_not_confirmed') ||
        errorMessage.toLowerCase().includes('email address not confirmed');
      
      if (isEmailNotConfirmed) {
        // Send OTP to complete email verification from signup
        setLoading(true);
        const { error: otpError } = await sendOtp(email.trim());
        if (otpError) {
          // Surface rate limit message: with seconds if available, else generic
          const rateLimited = (otpError as any)?.isRateLimited;
          const waitSeconds = (otpError as any)?.waitSeconds;
          if (rateLimited) {
            if (typeof waitSeconds === 'number') {
              setError(t('auth.otpResendRateLimited', { seconds: waitSeconds }));
            } else {
              setError(t('auth.otpRateLimitedGeneric'));
            }
          } else {
            setError(t('auth.otpResendError'));
          }
          setLoading(false);
        } else {
          setShowOtp(true);
          setError('');
          setLoading(false);
        }
      } else {
        setError(errorMessage || t('auth.signInError'));
        setLoading(false);
      }
      return;
    }
    // LoginPage will redirect after auth state updates.
  };

  const handleOtpVerified = () => {
    // OTP verified successfully - user is now logged in
    // LoginPage will redirect after auth state updates
    setShowOtp(false);
  };

  const handleBackFromOtp = () => {
    setShowOtp(false);
    setError('');
  };


  // Show OTP verification screen if email not confirmed (user didn't complete signup verification)
  if (showOtp) {
    return (
      <div className="bg-white rounded-2xl shadow-xl p-8">
        <OtpVerification
          email={email.trim()}
          onVerified={handleOtpVerified}
          onBack={handleBackFromOtp}
          type="signup"
          context="login"
        />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-xl p-8">
      <div className="text-center">
        <div className={`mx-auto h-12 w-12 ${authColors.iconGradient} rounded-full flex items-center justify-center mb-4`}>
          <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        </div>
        <h2 className="text-3xl font-bold text-gray-900 mb-2">
          {t('auth.loginTitle')}
        </h2>
      </div>

      <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
        <button
          type="button"
          onClick={async () => {
            setGoogleLoading(true);
            setError('');
            const { error: err } = await signInWithGoogle();
            if (err) {
              setError(err.message || t('auth.signInError'));
              setGoogleLoading(false);
            }
          }}
          disabled={loading || googleLoading}
          className="w-full flex items-center justify-center gap-3 py-3 px-4 border-2 border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 font-medium"
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          {googleLoading ? t('auth.signingIn') : t('auth.continueWithGoogle')}
        </button>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-200" />
          </div>
          <div className="relative flex justify-center">
            <span className="bg-white px-3 text-sm text-gray-500">{t('auth.orContinueWithEmail')}</span>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              {t('auth.emailAddress')}
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              disabled={loading}
              className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none"
              placeholder={t('auth.emailPlaceholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              {t('auth.password')}
            </label>
            <div className="relative">
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                required
                disabled={loading}
                className="w-full px-4 py-3.5 pr-12 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none"
                placeholder={t('auth.passwordPlaceholder')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                disabled={loading}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>
          
          {onSwitchToForgotPassword && (
            <div className="text-right">
              <button
                type="button"
                onClick={onSwitchToForgotPassword}
                className={`text-sm ${authColors.authLink}`}
              >
                {t('auth.forgotPassword')} ?
              </button>
            </div>
          )}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-red-700 text-sm text-center">{error}</p>
          </div>
        )}

        {loading && !error && (
          <LoadingWithHelp loading={true} inline={true} className="mt-4" />
        )}

        <div>
          <button
            type="submit"
            disabled={loading}
            className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-lg ${shadows.medium} text-sm font-medium text-white ${authColors.primaryButton} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {loading ? (
              <div className="flex items-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {t('auth.signingIn')}
              </div>
            ) : (
              t('auth.signIn')
            )}
          </button>
        </div>

        {showSignUpButton && onSwitchToSignUp && (
          <div className="text-center">
            <p className="text-sm text-gray-600">
              {t('auth.noAccount')}{' '}
              <button
                onClick={onSwitchToSignUp}
                className={authColors.authLink}
              >
                {t('auth.createAccount')}
              </button>
            </p>
          </div>
        )}
      </form>
      
      {/* Legal notice - required for GDPR compliance */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <p className="text-xs text-center text-gray-500">
          {t('auth.termsTextPrefix')}{' '}
          <Link to="/terms" className="text-blue-600 hover:text-blue-700 underline">
            {t('auth.termsLink')}
          </Link>
          {' '}{t('auth.termsTextAnd')}{' '}
          <Link to="/privacy" className="text-blue-600 hover:text-blue-700 underline">
            {t('auth.privacyLink')}
          </Link>
        </p>
      </div>
    </div>
  );
};

export default LoginForm;